/**
 * BaaS Contas → detalhe → Acessos: wallet users for a specific banking account.
 * MobileService `/v1/ib/baas/instances/{id}/accounts/{accountId}/users`.
 */

import { mobileConsoleRequest } from "./baasConsoleClient";
import {
  CARTEIRA_PERMISSION_GROUPS,
  CARTEIRA_ROLE_PERMISSIONS,
  carteiraUsersErrorMessage,
} from "./carteiraUsersApi";

const resolvePortalInstanceId = (target) => {
  if (target != null && typeof target === "object") {
    const numeric = Number(
      target.baasInstanceId ?? target.instanceId ?? target.instance_id,
    );
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
    if (target.id != null && target.id !== "") {
      return resolvePortalInstanceId(target.id);
    }
  }

  const raw = String(target ?? "");
  const separator = raw.indexOf(":");
  if (separator > 0) {
    const numeric = Number(raw.slice(0, separator));
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
  }

  const plain = Number(raw);
  if (Number.isFinite(plain) && plain > 0) return plain;

  const err = new Error("Invalid BaaS instance id for account users");
  err.name = "BaasConsoleError";
  err.code = "instance_invalid";
  throw err;
};

const accountUsersBasePath = (instance, accountId) => {
  const portalInstanceId = resolvePortalInstanceId(instance);
  const encodedAccount = encodeURIComponent(String(accountId ?? "").trim());
  return `v1/ib/baas/instances/${portalInstanceId}/accounts/${encodedAccount}/users`;
};

const mapUser = (row) => {
  const statusRaw = String(row?.status ?? "active").trim().toLowerCase();
  const status =
    statusRaw === "active" || statusRaw === "suspended" || statusRaw === "pending"
      ? statusRaw
      : "suspended";
  const roleRaw = String(row?.role ?? "admin").trim().toLowerCase();
  const role =
    roleRaw === "admin" || roleRaw === "operator" || roleRaw === "viewer" ? roleRaw : "admin";
  const permissions = Array.isArray(row?.permissions)
    ? row.permissions.map(String)
    : CARTEIRA_ROLE_PERMISSIONS[role] ?? [];

  return {
    id: String(row?.id ?? ""),
    personId: Number(row?.person_id ?? row?.personId) || null,
    name: String(row?.name ?? "").trim() || `Person ${row?.person_id ?? ""}`,
    email: String(row?.email ?? "").trim(),
    nationalRegistration: String(
      row?.national_registration ?? row?.nationalRegistration ?? "",
    ).trim(),
    accountId: row?.account_id ?? row?.accountId ?? null,
    role,
    permissions,
    status,
    invitePending: Boolean(row?.invite_pending ?? row?.invitePending ?? status === "pending"),
    lastAccess: row?.last_access ?? row?.lastAccess ?? null,
    createdAt: row?.created_at ?? row?.createdAt ?? null,
  };
};

const mapVerification = (row) => {
  if (!row) return null;
  return {
    required: Boolean(row.required ?? true),
    challengeId: row.challenge_id ?? row.challengeId ?? null,
    channel: String(row.channel ?? "email"),
    purpose: String(row.purpose ?? "wallet_invite"),
    expiresAt: row.expires_at ?? row.expiresAt ?? null,
    code: row.code ?? null,
  };
};

export { carteiraUsersErrorMessage };

export const fetchBaasAccountUsers = async (instance, accountId) => {
  const data = await mobileConsoleRequest({
    method: "GET",
    path: accountUsersBasePath(instance, accountId),
  });
  const items = Array.isArray(data?.items) ? data.items.map(mapUser) : [];
  return {
    items,
    manageSupported: Boolean(data?.manage_supported ?? data?.manageSupported),
    availablePermissions: Array.isArray(data?.available_permissions)
      ? data.available_permissions.map(String)
      : Array.isArray(data?.availablePermissions)
        ? data.availablePermissions.map(String)
        : CARTEIRA_PERMISSION_GROUPS.flatMap((g) => g.permissions.map((p) => p.id)),
  };
};

export const lookupBaasAccountPersonByDocument = async (
  instance,
  accountId,
  nationalRegistration,
) => {
  const digits = String(nationalRegistration ?? "").replace(/\D/g, "");
  const data = await mobileConsoleRequest({
    method: "GET",
    path: `${accountUsersBasePath(instance, accountId)}/lookup`,
    query: { national_registration: digits },
  });

  const person = data?.person;
  const userIdRaw = Number(data?.user_id ?? data?.userId);
  return {
    found: Boolean(data?.found),
    verified: Boolean(data?.verified),
    alreadyMember: Boolean(data?.already_member ?? data?.alreadyMember),
    hasPlatformAccess: Boolean(data?.has_platform_access ?? data?.hasPlatformAccess),
    accessStatus: String(data?.access_status ?? data?.accessStatus ?? "pending"),
    userId: Number.isFinite(userIdRaw) && userIdRaw > 0 ? userIdRaw : null,
    nationalRegistration: String(
      data?.national_registration ?? data?.nationalRegistration ?? digits,
    ),
    person: person
      ? {
          personId: Number(person.person_id ?? person.personId) || null,
          name: String(person.name ?? "").trim(),
          middleName: String(person.middle_name ?? person.middleName ?? "").trim(),
          lastName: String(person.last_name ?? person.lastName ?? "").trim(),
          email: String(person.email ?? "").trim(),
          nationalRegistration: String(
            person.national_registration ?? person.nationalRegistration ?? digits,
          ).replace(/\D/g, ""),
          dateOfBirth: String(person.date_of_birth ?? person.dateOfBirth ?? "").trim(),
          phone: String(person.phone ?? "").replace(/\D/g, ""),
          qualified: Boolean(person.qualified),
          phoneQualified: Boolean(person.phone_qualified ?? person.phoneQualified),
          hasPassword: Boolean(person.has_password ?? person.hasPassword),
        }
      : null,
  };
};

export const createBaasAccountUser = async (instance, accountId, payload) => {
  const phoneCountry = String(payload?.phoneCountryDialCode ?? "").replace(/\D/g, "");
  const phoneArea = String(payload?.phoneAreaCode ?? "").replace(/\D/g, "");
  const phoneLocal = String(payload?.phoneLocalNumber ?? "").replace(/\D/g, "");
  const phoneDigits =
    String(payload?.phone ?? "").replace(/\D/g, "") ||
    (phoneCountry && phoneArea && phoneLocal ? `${phoneCountry}${phoneArea}${phoneLocal}` : "");

  const data = await mobileConsoleRequest({
    method: "POST",
    path: accountUsersBasePath(instance, accountId),
    body: {
      email: payload?.email,
      name: payload?.name,
      middle_name: payload?.middleName,
      last_name: payload?.lastName,
      national_registration: payload?.nationalRegistration,
      phone: phoneDigits || undefined,
      phone_country_dial_code: phoneCountry || undefined,
      phone_area_code: phoneArea || undefined,
      phone_local_number: phoneLocal || undefined,
      date_of_birth: payload?.dateOfBirth,
      role: payload?.role,
      permissions: payload?.permissions,
      status: payload?.status ?? "active",
    },
  });

  if (data?.user) {
    return {
      user: mapUser(data.user),
      personCreated: Boolean(data.person_created ?? data.personCreated),
      inviteKind: String(data.invite_kind ?? data.inviteKind ?? "").trim() || "first_access",
      accessStatus: String(data.access_status ?? data.accessStatus ?? "pending"),
      emailSent: Boolean(data.email_sent ?? data.emailSent),
      verification: mapVerification(data.verification),
    };
  }

  return {
    user: mapUser(data),
    personCreated: false,
    inviteKind: "first_access",
    accessStatus: "pending",
    emailSent: false,
    verification: null,
  };
};

export const updateBaasAccountUser = async (instance, accountId, userId, payload) => {
  const data = await mobileConsoleRequest({
    method: "PATCH",
    path: `${accountUsersBasePath(instance, accountId)}/${userId}`,
    body: {
      role: payload?.role,
      permissions: payload?.permissions,
      status: payload?.status,
    },
  });
  return mapUser(data);
};

export const updateBaasAccountUserByPerson = async (
  instance,
  accountId,
  personId,
  payload,
) => {
  const data = await mobileConsoleRequest({
    method: "PATCH",
    path: `${accountUsersBasePath(instance, accountId)}/person/${personId}`,
    body: {
      role: payload?.role,
      permissions: payload?.permissions,
      status: payload?.status,
    },
  });
  return mapUser(data);
};

export const saveBaasAccountUser = async (instance, accountId, user, payload) => {
  const walletId = Number(user?.id);
  if (Number.isFinite(walletId) && walletId > 0) {
    return updateBaasAccountUser(instance, accountId, walletId, payload);
  }
  const personId = Number(user?.personId);
  if (Number.isFinite(personId) && personId > 0) {
    return updateBaasAccountUserByPerson(instance, accountId, personId, payload);
  }
  throw new Error("Usuário sem identificador para edição.");
};

export const removeBaasAccountUser = async (instance, accountId, userId) => {
  await mobileConsoleRequest({
    method: "DELETE",
    path: `${accountUsersBasePath(instance, accountId)}/${userId}`,
  });
};

export const removeBaasAccountUserByPerson = async (instance, accountId, personId) => {
  await mobileConsoleRequest({
    method: "DELETE",
    path: `${accountUsersBasePath(instance, accountId)}/person/${personId}`,
  });
};

export const revokeBaasAccountUser = async (instance, accountId, user) => {
  const walletId = Number(user?.id);
  if (Number.isFinite(walletId) && walletId > 0) {
    await removeBaasAccountUser(instance, accountId, walletId);
    return;
  }
  const personId = Number(user?.personId);
  if (Number.isFinite(personId) && personId > 0) {
    await removeBaasAccountUserByPerson(instance, accountId, personId);
    return;
  }
  throw new Error("Usuário sem identificador para remoção.");
};
